import { AgentOrClientOrderStatusForTable } from 'components/AgentOrClientOrderStatusForTable';
import { Tab } from 'components/redesign/Tabs/types';
import { OrderDetailsStatusEnum } from 'services/AgentService/types';
import { formatAmount, getDateWithTimezone } from 'utils/helpers';
import { CustomerRepresentativeOrderTabsEnum } from './types';
import { Tooltip } from 'react-tooltip';
import classNames from 'classnames';
import { ROUBLE_SIGN } from 'utils/settings';

export const customerRepresentativeTabs: Tab<CustomerRepresentativeOrderTabsEnum>[] = [
  {
    link: `/client/customerrepresentative/orders/${CustomerRepresentativeOrderTabsEnum.awaiting}`,
    orderType: CustomerRepresentativeOrderTabsEnum.awaiting,
    name: 'Ожидающие оплаты ',
  },
  {
    link: `/client/customerrepresentative/orders/${CustomerRepresentativeOrderTabsEnum.active}`,
    orderType: CustomerRepresentativeOrderTabsEnum.active,
    name: 'Требующие действий',
  },
  {
    link: `/client/customerrepresentative/orders/${CustomerRepresentativeOrderTabsEnum.unconfirmed}`,
    orderType: CustomerRepresentativeOrderTabsEnum.unconfirmed,
    name: 'С неподтверждёнными оплатами ',
  },
  {
    link: `/client/customerrepresentative/orders/${CustomerRepresentativeOrderTabsEnum.blocked}`,
    orderType: CustomerRepresentativeOrderTabsEnum.blocked,
    name: 'С заблокированными пользователями ',
  },
  {
    link: `/client/customerrepresentative/orders/${CustomerRepresentativeOrderTabsEnum.canceled}`,
    orderType: CustomerRepresentativeOrderTabsEnum.canceled,
    name: 'Отменённые заявки',
  },
  {
    link: `/client/customerrepresentative/orders/${CustomerRepresentativeOrderTabsEnum.paid}`,
    orderType: CustomerRepresentativeOrderTabsEnum.paid,
    name: 'Выплаченные заявки',
  },
];

const repeatingTableHeadings: TableHeading[] = [
  {
    attributeName: 'number',
    name: 'Номер заявки',
    renderColumn: (column) => <span className="whitespace-nowrap">{column as string}</span>,
  },
  {
    attributeName: 'creationTime',
    name: 'Дата создания',
    renderColumn: (column) => (
      <span className="whitespace-nowrap">
        {getDateWithTimezone(String(column), undefined, 'dd.MM.yyyy HH:mm')}
      </span>
    ),
  },
  {
    attributeName: 'paymentAmount',
    name: 'Сумма выплаты',
    renderColumn: (column) => (
      <span className="whitespace-nowrap">
        {formatAmount(+column, { minimumFractionDigits: 2 })}&nbsp;{ROUBLE_SIGN}
      </span>
    ),
  },
  {
    attributeName: 'statusUpdateTime',
    name: 'Дата перехода в состояние',
    renderColumn: (column) => (
      <span className="whitespace-nowrap">
        {getDateWithTimezone(String(column), undefined, 'dd.MM.yyyy HH:mm')}
      </span>
    ),
  },
  {
    attributeName: 'cityName',
    name: 'Населенный пункт',
    renderColumn: (column) => <span className="whitespace-nowrap">{column as string}</span>,
  },
];

const awaitingOrdersTableHeadings: TableHeading[] = [
  ...repeatingTableHeadings,
  {
    attributeName: 'agentOrganizationFullName',
    name: 'Исполнитель',
    renderColumn: (column, rowData) => {
      const blocked = rowData.ftsStatusName !== 'Проверки ФНС пройдены';
      return (
        <>
          <span
            className={classNames('whitespace-nowrap', {
              'text-danger': blocked,
            })}
            data-tooltip-id="ftsStatusName-tooltip"
            data-tooltip-content={String(rowData.ftsStatusName)}
          >
            {blocked && '! '}
            {column as string}
          </span>
          <Tooltip id="ftsStatusName-tooltip" place="bottom" style={{ zIndex: 9999 }} />
        </>
      );
    },
  },
];

const activeOrdersTableHeadings: TableHeading[] = [
  ...repeatingTableHeadings,
  {
    attributeName: 'initiatorFullName',
    name: 'Инициатор оплаты',
    renderColumn: (column) => <span className="whitespace-nowrap">{column as string}</span>,
  },
  {
    attributeName: 'agentOrganizationFullName',
    name: 'Исполнитель',
    renderColumn: (column, rowData) => {
      const blocked = rowData.ftsStatusName !== 'Проверки ФНС пройдены';
      return (
        <>
          <span
            className={classNames('whitespace-nowrap', {
              'text-danger': blocked,
            })}
            data-tooltip-id="ftsStatusName-tooltip"
            data-tooltip-content={String(rowData.ftsStatusName)}
          >
            {blocked && '! '}
            {column as string}
          </span>
          <Tooltip id="ftsStatusName-tooltip" place="bottom" style={{ zIndex: 9999 }} />
        </>
      );
    },
  },
  {
    attributeName: 'status',
    name: 'Состояние',
    renderColumn: (column) => (
      <AgentOrClientOrderStatusForTable status={column as OrderDetailsStatusEnum} />
    ),
  },
];

const unconfirmedOrdersTableHeadings: TableHeading[] = [
  ...repeatingTableHeadings,
  {
    attributeName: 'initiatorFullName',
    name: 'Инициатор оплаты',
    renderColumn: (column) => <span className="whitespace-nowrap">{column as string}</span>,
  },
  {
    attributeName: 'agentOrganizationFullName',
    name: 'Исполнитель',
    renderColumn: (column, rowData) => {
      const blocked = rowData.ftsStatusName !== 'Проверки ФНС пройдены';
      return (
        <>
          <span
            className={classNames('whitespace-nowrap', {
              'text-danger': blocked,
            })}
            data-tooltip-id="ftsStatusName-tooltip"
            data-tooltip-content={String(rowData.ftsStatusName)}
          >
            {blocked && '! '}
            {column as string}
          </span>
          <Tooltip id="ftsStatusName-tooltip" place="bottom" style={{ zIndex: 9999 }} />
        </>
      );
    },
  },
  {
    attributeName: 'receiptStatusName',
    name: 'Статус чека',
    renderColumn: (column) => (
      <span
        className={classNames(
          'block w-max rounded-[4px] bg-danger px-2 py-[1px] text-sm text-white'
        )}
      >
        {column as string}
      </span>
    ),
  },
];

const blockedOrdersTableHeadings: TableHeading[] = [
  ...repeatingTableHeadings,
  {
    attributeName: 'initiatorFullName',
    name: 'Инициатор оплаты',
    renderColumn: (column) => <span className="whitespace-nowrap">{column as string}</span>,
  },
  {
    attributeName: 'agentOrganizationFullName',
    name: 'Исполнитель',
    renderColumn: (column, rowData) => {
      const blocked = rowData.ftsStatusName !== 'Проверки ФНС пройдены';
      return (
        <>
          <span
            className={classNames('whitespace-nowrap', {
              'text-danger': blocked,
            })}
            data-tooltip-id="ftsStatusName-tooltip"
            data-tooltip-content={String(rowData.ftsStatusName)}
          >
            {blocked && '! '}
            {column as string}
          </span>
          <Tooltip id="ftsStatusName-tooltip" place="bottom" style={{ zIndex: 9999 }} />
        </>
      );
    },
  },
  {
    attributeName: 'status',
    name: 'Причина блокировки',
    renderColumn: (column) => (
      <AgentOrClientOrderStatusForTable status={column as OrderDetailsStatusEnum} />
    ),
  },
];

const canceledOrdersTableHeadings: TableHeading[] = [
  ...repeatingTableHeadings,
  {
    attributeName: 'agentOrganizationFullName',
    name: 'Исполнитель',
    renderColumn: (column, rowData) => {
      const blocked = rowData.ftsStatusName !== 'Проверки ФНС пройдены';
      return (
        <>
          <span
            className={classNames('whitespace-nowrap', {
              'text-danger': blocked,
            })}
            data-tooltip-id="ftsStatusName-tooltip"
            data-tooltip-content={String(rowData.ftsStatusName)}
          >
            {blocked && '! '}
            {column as string}
          </span>
          <Tooltip id="ftsStatusName-tooltip" place="bottom" style={{ zIndex: 9999 }} />
        </>
      );
    },
  },
];

const paidOrdersTableHeadings: TableHeading[] = [
  ...repeatingTableHeadings,
  {
    attributeName: 'initiatorFullName',
    name: 'Инициатор оплаты',
    renderColumn: (column) => <span className="whitespace-nowrap">{column as string}</span>,
  },
  {
    attributeName: 'agentOrganizationFullName',
    name: 'Исполнитель',
    renderColumn: (column, rowData) => {
      const blocked = rowData.ftsStatusName !== 'Проверки ФНС пройдены';
      return (
        <>
          <span
            className={classNames('whitespace-nowrap', {
              'text-danger': blocked,
            })}
            data-tooltip-id="ftsStatusName-tooltip"
            data-tooltip-content={String(rowData.ftsStatusName)}
          >
            {blocked && '! '}
            {column as string}
          </span>
          <Tooltip id="ftsStatusName-tooltip" place="bottom" style={{ zIndex: 9999 }} />
        </>
      );
    },
  },
  {
    attributeName: 'status',
    name: 'Состояние',
    renderColumn: (column) => (
      <AgentOrClientOrderStatusForTable status={column as OrderDetailsStatusEnum} />
    ),
  },
];

export const customerRepresentativeWorkTableHeadings = {
  [CustomerRepresentativeOrderTabsEnum.awaiting]: awaitingOrdersTableHeadings,
  [CustomerRepresentativeOrderTabsEnum.active]: activeOrdersTableHeadings,
  [CustomerRepresentativeOrderTabsEnum.unconfirmed]: unconfirmedOrdersTableHeadings,
  [CustomerRepresentativeOrderTabsEnum.blocked]: blockedOrdersTableHeadings,
  [CustomerRepresentativeOrderTabsEnum.canceled]: canceledOrdersTableHeadings,
  [CustomerRepresentativeOrderTabsEnum.paid]: paidOrdersTableHeadings,
};
